import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from './api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { DataView } from '@/lib/ui/states';
import { Card } from '@/lib/ui/Card';
import { Button } from '@/lib/ui/Button';
import { Select } from '@/lib/ui/Select';
import { TextInput, TextArea } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { useToast } from '@/lib/ui';
import type { Locale, SettingsDocument } from '@/lib/api/types';

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
function asBool(v: unknown): boolean {
  return v === true;
}
function asLocale(v: unknown): Locale {
  return v === 'ar' ? 'ar' : 'en';
}
function asLocaleList(v: unknown): Locale[] {
  if (!Array.isArray(v)) return ['en', 'ar'];
  return v.filter((x): x is Locale => x === 'en' || x === 'ar');
}
function asI18n(v: unknown): { en: string; ar: string } {
  const o = (v ?? {}) as Record<string, unknown>;
  return { en: asString(o.en), ar: asString(o.ar) };
}

/** Settings tab — full-document read-modify-write of the site settings (spec §15.2). */
export function SettingsTab() {
  const query = useQuery({
    queryKey: keys.settings.doc(),
    queryFn: () => settingsApi.getSettings(),
  });

  return (
    <DataView query={query}>{(doc) => <SettingsForm doc={doc} />}</DataView>
  );
}

function SettingsForm({ doc }: { doc: SettingsDocument }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();

  const LOCALE_OPTIONS = [
    { value: 'en', label: t('settings.general.localeEn') },
    { value: 'ar', label: t('settings.general.localeAr') },
  ];

  const [defaultLocale, setDefaultLocale] = useState<Locale>(asLocale(doc.default_locale));
  const [enabledLocales, setEnabledLocales] = useState<Locale[]>(
    asLocaleList(doc.enabled_locales),
  );
  const [requireAr, setRequireAr] = useState<boolean>(asBool(doc.require_ar_on_publish));
  const [maintenance, setMaintenance] = useState<boolean>(asBool(doc.maintenance_mode));
  const [headScripts, setHeadScripts] = useState<string>(asString(doc.head_scripts));
  const [logoId, setLogoId] = useState<string>(asString(doc.brand_logo_id));
  const [faviconId, setFaviconId] = useState<string>(asString(doc.favicon_id));
  // Brand identity (Phase-1 `/site` projection): wordmark per script, localized
  // tagline, canonical URL. The public chrome reads these via GET /api/v1/site.
  const [wordmark, setWordmark] = useState<string>(asString(doc.wordmark));
  const [wordmarkAr, setWordmarkAr] = useState<string>(asString(doc.wordmark_ar));
  const [tagline, setTagline] = useState(asI18n(doc.tagline));
  const [siteUrl, setSiteUrl] = useState<string>(asString(doc.site_url));

  const enToggle = (l: Locale, on: boolean) =>
    setEnabledLocales((prev) => {
      const next = new Set(prev);
      if (on) next.add(l);
      else next.delete(l);
      // Always keep at least the default locale enabled.
      next.add(defaultLocale);
      return (['en', 'ar'] as Locale[]).filter((x) => next.has(x));
    });

  const dirty = useMemo(
    () =>
      defaultLocale !== asLocale(doc.default_locale) ||
      JSON.stringify(enabledLocales) !== JSON.stringify(asLocaleList(doc.enabled_locales)) ||
      requireAr !== asBool(doc.require_ar_on_publish) ||
      maintenance !== asBool(doc.maintenance_mode) ||
      headScripts !== asString(doc.head_scripts) ||
      logoId !== asString(doc.brand_logo_id) ||
      faviconId !== asString(doc.favicon_id) ||
      wordmark !== asString(doc.wordmark) ||
      wordmarkAr !== asString(doc.wordmark_ar) ||
      JSON.stringify(tagline) !== JSON.stringify(asI18n(doc.tagline)) ||
      siteUrl !== asString(doc.site_url),
    [
      doc,
      defaultLocale,
      enabledLocales,
      requireAr,
      maintenance,
      headScripts,
      logoId,
      faviconId,
      wordmark,
      wordmarkAr,
      tagline,
      siteUrl,
    ],
  );

  const save = useMutation({
    // Send ONLY the fields the user changed. The backend PUT is a field-level
    // read-modify-write merge (it loads the current singleton and decodes the
    // body over it), so omitted keys are preserved untouched. Sending the whole
    // document is both unnecessary and unsafe: it decodes with
    // DisallowUnknownFields, so any key the server doesn't recognise rejects the
    // entire request with 422 — and the field names below are the server's
    // (brand_logo_id, favicon_id), not the form's local state names.
    mutationFn: () => {
      const changes: Partial<SettingsDocument> = {};
      if (defaultLocale !== asLocale(doc.default_locale))
        changes.default_locale = defaultLocale;
      if (
        JSON.stringify(enabledLocales) !==
        JSON.stringify(asLocaleList(doc.enabled_locales))
      )
        changes.enabled_locales = enabledLocales;
      if (requireAr !== asBool(doc.require_ar_on_publish))
        changes.require_ar_on_publish = requireAr;
      if (maintenance !== asBool(doc.maintenance_mode))
        changes.maintenance_mode = maintenance;
      if (headScripts !== asString(doc.head_scripts))
        changes.head_scripts = headScripts;
      if (logoId !== asString(doc.brand_logo_id))
        changes.brand_logo_id = logoId || null;
      if (faviconId !== asString(doc.favicon_id))
        changes.favicon_id = faviconId || null;
      if (wordmark !== asString(doc.wordmark)) changes.wordmark = wordmark;
      if (wordmarkAr !== asString(doc.wordmark_ar)) changes.wordmark_ar = wordmarkAr;
      if (JSON.stringify(tagline) !== JSON.stringify(asI18n(doc.tagline)))
        changes.tagline = tagline;
      if (siteUrl !== asString(doc.site_url)) changes.site_url = siteUrl;
      return settingsApi.putSettings(changes);
    },
    onSuccess: (next) => {
      qc.setQueryData(keys.settings.doc(), next);
      void qc.invalidateQueries({ queryKey: keys.settings.doc() });
      toast.success(t('settings.general.savedTitle'), t('settings.general.savedDetail'));
    },
    onError: (e) => onError(e),
  });

  return (
    <div className="flex max-w-3xl flex-col gap-lg">
      <Card>
        <div className="flex flex-col gap-md">
          <h2 className="text-title text-ink-deep">{t('settings.general.localesHeading')}</h2>
          <Select
            label={t('settings.general.defaultLocale')}
            value={defaultLocale}
            onValueChange={(v) => setDefaultLocale(asLocale(v))}
            options={LOCALE_OPTIONS}
          />
          <div className="flex flex-col gap-sm">
            <span className="eyebrow text-ink-mute">{t('settings.general.enabledLocales')}</span>
            <Switch
              label={t('settings.general.localeEn')}
              checked={enabledLocales.includes('en')}
              onCheckedChange={(v) => enToggle('en', v)}
              disabled={defaultLocale === 'en'}
            />
            <Switch
              label={t('settings.general.localeAr')}
              checked={enabledLocales.includes('ar')}
              onCheckedChange={(v) => enToggle('ar', v)}
              disabled={defaultLocale === 'ar'}
            />
          </div>
          <Switch
            label={t('settings.general.requireAr')}
            hint={t('settings.general.requireArHint')}
            checked={requireAr}
            onCheckedChange={setRequireAr}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-md">
          <h2 className="text-title text-ink-deep">
            {t('settings.general.maintenanceHeading')}
          </h2>
          <Switch
            label={t('settings.general.maintenanceToggle')}
            hint={t('settings.general.maintenanceHint')}
            checked={maintenance}
            onCheckedChange={setMaintenance}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-md">
          <h2 className="text-title text-ink-deep">
            {t('settings.general.brandIdentityHeading')}
          </h2>
          <p className="text-small text-ink-mute">
            {t('settings.general.brandIdentityDescription')}
          </p>
          <TextInput
            label={t('settings.general.wordmark')}
            hint={t('settings.general.wordmarkHint')}
            value={wordmark}
            onChange={(e) => setWordmark(e.target.value)}
          />
          <TextInput
            label={t('settings.general.wordmarkAr')}
            hint={t('settings.general.wordmarkArHint')}
            value={wordmarkAr}
            onChange={(e) => setWordmarkAr(e.target.value)}
            dir="rtl"
          />
          <TextInput
            label={`${t('settings.general.tagline')} (EN)`}
            hint={t('settings.general.taglineHint')}
            value={tagline.en}
            onChange={(e) => setTagline((p) => ({ ...p, en: e.target.value }))}
          />
          <TextInput
            label={`${t('settings.general.tagline')} (AR)`}
            value={tagline.ar}
            onChange={(e) => setTagline((p) => ({ ...p, ar: e.target.value }))}
            dir="rtl"
          />
          <TextInput
            label={t('settings.general.siteUrl')}
            hint={t('settings.general.siteUrlHint')}
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            inputMode="url"
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-md">
          <h2 className="text-title text-ink-deep">{t('settings.general.brandHeading')}</h2>
          <p className="text-small text-ink-mute">
            {t('settings.general.brandDescription')}
          </p>
          <TextInput
            label={t('settings.general.logoAssetId')}
            value={logoId}
            onChange={(e) => setLogoId(e.target.value)}
          />
          <TextInput
            label={t('settings.general.faviconAssetId')}
            value={faviconId}
            onChange={(e) => setFaviconId(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-md">
          <h2 className="text-title text-ink-deep">{t('settings.general.headScriptsHeading')}</h2>
          <div
            role="alert"
            className="rounded-md border-s-[3px] border-signal-error border-[0.5px] border-hairline bg-signal-error-fill px-md py-sm text-small text-signal-error"
          >
            {t('settings.general.headScriptsWarning')}
          </div>
          <TextArea
            label={t('settings.general.headScriptsLabel')}
            value={headScripts}
            onChange={(e) => setHeadScripts(e.target.value)}
            className="min-h-[160px] font-mono text-small"
            spellCheck={false}
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate(undefined)}>
          {t('settings.general.saveButton')}
        </Button>
      </div>
    </div>
  );
}
